import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, Check, Plus, Save, Search, Key, Users, Building2,
} from '../../icons';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button, Tabs, Modal, ModalActions, FormField, Input, Select } from '../../components/shared';
import { PersonHead } from './PersonHead';
import {
  listEmployees, getEmployee, updateEmployeeHr, hireEmployee,
  EMPLOYMENT_TYPE_LABEL, EMPLOYMENT_STATUS_LABEL,
  type EmployeeRowDto, type EmployeeDetailDto,
} from '../../api/hr-employee';
import {
  listWorkSchedules, saveWorkAssignments, WORK_TYPE_LABEL, type WorkScheduleDto,
} from '../../api/hr-schedule';
import { SCHEDULE_SOURCE_LABEL } from '../../api/hr-people';
import { listDivisions, listTeams, type OrgDivision, type OrgTeam } from '../../api/admin-org';
import { changeUserRole, resetAdminUserPassword, type AssignableRole } from '../../api/admin-user';
import { addTeamMember, removeTeamMember, setTeamMemberLeader } from '../../api/admin-org';
import { assignableRolesFor, canManageRole, ROLE_OPTION_LABEL, type ManagedRole } from '../../lib/role-rank';
import { useAuthStore } from '../../store/authStore';
import { JobGradeSelect } from '../../components/JobGradeSelect';
import './HrShell.css';
import './StaffPage.css';
// 기준 설정 (2026-08-28 · 판 7) — 고용형태의 «이름»과 «씀 여부»는 회사가 정한다.
//   못 읽으면 기본 목록(EMPLOYMENT_TYPE_LABEL)이 그대로 산다.
import { useCompanyCodes } from '../../lib/company-codes';
// 대표 · 부서장 · 팀장 — 시안의 이름 옆 배지. 이미 있는 라우트를 그대로 읽는다.
import { getOrgLeaders } from '../../api/hr-org-leader';
// 시안 B2 — 기록 다섯은 인사 카드와 **같은 한 벌**을 쓴다(복사하면 한쪽만 고쳐진다).
import { StaffRecordSection, RECORD_TABS, type RecordTab } from './StaffRecordSection';
// 우측 「휴가 · 연차」 카드 — 연차 관리 화면이 쓰는 그 목록에서 이 사람 줄만 골라 쓴다.
import { listLeaveQuotas, type QuotaRowDto } from '../../api/hr-admin';

/**
 * 상세의 탭 — **시안 B2 그대로 일곱**이다.
 * ★ 기록 다섯은 인사 카드와 같은 목록(`RECORD_TABS`)을 쓴다 — 이름이 두 화면에서 갈리면
 *   같은 것을 다른 것으로 읽게 된다.
 */
type DetailTab = 'PROFILE' | RecordTab | 'ACCOUNT';
const DETAIL_TABS: ReadonlyArray<{ key: DetailTab; label: string }> = [
  /* ★ 「인사 정보」 — 고용형태 · 근무제 · 사번 · 입사일 · 근속이 여기 다 선다(2026-09-02). */
  { key: 'PROFILE', label: '인사 정보' },
  ...RECORD_TABS,
  { key: 'ACCOUNT', label: '계정 · 권한' },
];

/** 왼쪽 조직도에서 고른 자리 — 회사 전체 · 부서 · 팀 · 회사 직속. */
type OrgSel =
  | { kind: 'all' }
  | { kind: 'div'; id: string }
  | { kind: 'team'; id: string }
  | { kind: 'none' };

/**
 * 직원 관리 — **명부 · 계정 · 권한 · 부서를 한 화면에서** (2026-08-27, 사용자 지시).
 *
 * ★★ **«직원 명부» 와 «사용자 관리» 를 합친 자리다.** 둘로 갈라 놓으니 사람 하나를 넣는 데
 *    화면을 두 번 열어야 했다 — 인사 정보는 이쪽, 권한과 부서는 저쪽. 옛 «직원 명부» 가
 *    이미 그 문제를 알고 있었다(「화면을 나누지 말고 동작을 나눈다」). 그 말을 끝까지 지킨다.
 *
 * ★★ **무엇을 고칠 수 있는지는 서버가 정한다**(`editable`). 화면이 스스로 정하면 서버와 어긋나
 *    「눌렀는데 403」 이 된다. 권한 바꾸기도 마찬가지 — **내 등급 이하만** 고를 수 있고
 *    (`assignableRolesFor`), 마지막 관리자는 서버가 막는다.
 *
 * ★★ **감사는 한 줄기다.** 권한 · 비밀번호 · 부서는 관리 콘솔이 쓰는 **바로 그 라우트**를
 *    부른다. 화면이 둘이어도 기록은 하나여야 「누가 언제 무엇을」 을 이어서 읽을 수 있다.
 *
 * ★ **초기 비밀번호는 만든 그 순간 한 번만** 보인다 — 저장하지 않으므로 다시 볼 수 없다.
 * ★ 옛 두 화면은 **그대로 둔다**(관리 콘솔이 `UserManageWorkspace` 를 함께 쓴다) —
 *   이 화면은 인사 트리에 새로 서는 것이지 그것들을 지운 것이 아니다.
 */

const ERRORS: Record<string, string> = {
  ACCOUNT_FIELD_NOT_HERE: '계정 칸은 관리 콘솔의 사용자 관리에서 고칩니다.',
  EMAIL_EXISTS: '이미 있는 이메일입니다.',
  EMAIL_EXISTS_DELETED: '지운 계정이 같은 이메일을 쓰고 있습니다 — 관리 콘솔에서 되살려 주세요.',
  EMAIL_INVALID: '이메일 형식이 아닙니다.',
  INVALID_DATE: '없는 날짜입니다.',
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
  LAST_ADMIN: '마지막 관리자의 권한은 내릴 수 없습니다.',
  ROLE_TOO_HIGH: '내 등급보다 높은 권한은 줄 수 없습니다.',
};

const codeOf = (err: unknown) =>
  (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';

interface HireForm {
  name: string; email: string; employeeNumber: string; hireDate: string;
  position: string; employmentType: string; teamId: string;
}
const EMPTY_HIRE: HireForm = {
  name: '', email: '', employeeNumber: '', hireDate: '',
  position: '', employmentType: 'REGULAR', teamId: '',
};

export function StaffPage(): JSX.Element {
  // ★ 고용형태 이름은 회사가 정한다(「단시간」을 「시간제」로 부르는 곳이 있다).
  const codes = useCompanyCodes('hr');
  const employmentTypes = codes.apply(
    'employment_type',
    Object.entries(EMPLOYMENT_TYPE_LABEL).map(([key, label]) => ({ key, label })),
  );

  const myRole = useAuthStore((s) => s.user?.role ?? null);

  const [rows, setRows] = useState<EmployeeRowDto[]>([]);
  const [missing, setMissing] = useState(0);
  const [search, setSearch] = useState('');
  /**
   * ★★★ **왼쪽에서 고른 조직 하나** (2026-08-31 · 시안 B1 맞춤).
   *    종전은 «팀 id» 하나였고 서버에 다시 물었다 — 그래서 **부서를 고를 수가 없었고**
   *    (부서는 라벨일 뿐이었다) 「미배정」 줄도 없었다.
   * ★ 이제 **한 번 받아 화면이 거른다** — 왕복이 안 늘고 **위아래 숫자가 어긋날 일도 없다**
   *   (근태 판 F 에서 세운 규칙 그대로). 서버 목록에 쪽 나눔이 없어 받는 양도 같다.
   */
  const [orgSel, setOrgSel] = useState<OrgSel>({ kind: 'all' });
  /** 왼쪽 칸의 찾기 — 시안의 「부서 · 팀 찾기」. 사람 찾기(오른쪽)와 다른 일이다. */
  const [treeQ, setTreeQ] = useState('');
  /**
   * ★★★ **끌어다 놓는 소속 이동** (2026-08-31 · 시안 B1 callout 1).
   *    시안: 「사람 행을 **팀 위로 끌어다 놓으면** 소속이 바뀝니다.」
   *    통합설정의 조직도에서 이미 도는 방식이라 **서버 일이 0** 이다 — 다만 서버에 «옮기기»가
   *    없어 **빼고 넣기** 두 단계로 돈다(그쪽 `moveUsersToTeam` 과 같은 순서).
   * ★ 끌고 있는 사람은 **ref** 에 둔다 — 다시 그리게 할 값이 아니다.
   */
  const dragUser = useRef<EmployeeRowDto | null>(null);
  /** 지금 올라가 있는 팀 — 떨굴 자리를 눈에 보이게. 이것만 상태다. */
  const [dropTeam, setDropTeam] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<EmployeeDetailDto | null>(null);
  const [hrDraft, setHrDraft] = useState<Record<string, string>>({});
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  /**
   * 조직 트리 (2026-08-30 · 시안 맞춤) — 시안은 왼쪽이 **부서 · 팀**이다.
   * ★ 종전은 「부서 전체」 드롭다운 하나라 **어느 팀에 몇 명인지**를 볼 수 없었다.
   */
  const [divs, setDivs] = useState<OrgDivision[]>([]);
  const [orgTeams, setOrgTeams] = useState<OrgTeam[]>([]);
  /** 재직 · 휴직 · 퇴사 — 서버가 이미 `status` 를 받는다(새 라우트 0). */
  const [status, setStatus] = useState<'ACTIVE' | 'LEAVE' | 'RESIGNED'>('ACTIVE');
  const [mode, setMode] = useState<'detail' | 'hire'>('detail');
  const [hire, setHire] = useState<HireForm>(EMPTY_HIRE);
  const [issued, setIssued] = useState<{ password: string; annualDays: number | null; who: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [moveTeam, setMoveTeam] = useState('');
  /** 상세의 탭 — 사람을 바꾸면 「신상」으로 돌아온다(앞사람 탭에 머물면 헷갈린다). */
  const [dtab, setDtab] = useState<DetailTab>('PROFILE');
  /**
   * 연차 한 줄 — 시안 B2 의 오른쪽 「휴가 · 연차」 카드.
   * ★ 연차 관리 화면이 쓰는 **그 목록**을 한 번 읽어 이 사람 줄만 고른다(새 라우트 0).
   *   못 읽으면 카드만 안 뜬다.
   */
  const [quotas, setQuotas] = useState<QuotaRowDto[] | null>(null);

  const load = useCallback(async (q?: string, st?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listEmployees({
        ...(q ? { search: q } : {}),
        // ★ 팀은 **안 보낸다** — 조직 고르기는 화면이 한다(위 `orgSel` 참조).
        //   그래야 트리의 숫자와 목록의 숫자가 한 벌에서 나온다.
        // ★ 재직 말고 다른 것을 볼 때만 보낸다 — 옛 서버는 이 칸을 모른다(기본은 재직).
        ...(st && st !== 'ACTIVE' ? { status: st } : {}),
      });
      setRows(data.rows);
      setMissing(data.missingHireDate);
    } catch (err) {
      setError(ERRORS[codeOf(err)] ?? '직원 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 대표 · 부서장 · 팀장 — **시안의 이름 옆 배지**(2026-08-31).
   * ★ 명부 행에는 그 값이 없다(`EmployeeRowDto` 에 `isLeader` 가 없다) — 이미 있는 라우트에서
   *   한 번 읽어 곁들인다. 못 읽으면 배지만 안 붙고 목록은 제 힘으로 산다.
   */
  const [repId, setRepId] = useState<string | null>(null);
  const [divLeadIds, setDivLeadIds] = useState<ReadonlySet<string>>(new Set());
  const [teamLeadIds, setTeamLeadIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    void getOrgLeaders()
      .then((L) => {
        setRepId(L.representative?.userId ?? null);
        setDivLeadIds(new Set(L.divisions.map((d) => d.leaderUserId).filter((v): v is string => !!v)));
        setTeamLeadIds(new Set(L.teams.map((t) => t.leaderUserId).filter((v): v is string => !!v)));
      })
      .catch(() => { /* 배지만 안 붙는다 */ });
  }, []);
  useEffect(() => {
    /* 팀 목록은 근무제 화면과 같은 API 를 쓴다 — 목록을 새로 만들지 않는다.
       ★ 2026-09-02 — **근무제 목록도 같은 응답에 들어 있다.** 요청을 늘리지 않고 담는다.
         중지한 것은 뺀다: 중지한 근무제에는 새로 배정할 수 없다(서버가 판정에서도 뺀다). */
    listWorkSchedules()
      .then((d) => { setTeams(d.teams); setSchedules(d.schedules.filter((x) => x.isActive)); })
      .catch(() => { /* 배정 칸만 못 쓴다 */ });
    // ★ 못 읽으면 트리만 접힌다 — 목록은 제 힘으로 산다.
    void Promise.all([listDivisions(), listTeams()])
      .then(([d, t]) => { setDivs(d.filter((x) => x.isActive)); setOrgTeams(t.filter((x) => x.isActive)); })
      .catch(() => { setDivs([]); setOrgTeams([]); });
  }, []);

  /** 팀 id → 부서 이름. 시안의 소속 칸이 「AX본부 · 블록체인팀」 두 마디이기 때문이다. */
  const divNameOfTeam = useMemo(() => {
    const byDiv = new Map(divs.map((d) => [d.id, d.name]));
    return new Map(orgTeams.map((t) => [t.id, t.divisionId ? byDiv.get(t.divisionId) ?? null : null]));
  }, [divs, orgTeams]);

  /** 이 사람이 이 조직에 드는가 — 부서를 고르면 그 부서의 팀 전부가 든다. */
  const inSel = useCallback((r: EmployeeRowDto, sel: OrgSel): boolean => {
    if (sel.kind === 'all') return true;
    if (sel.kind === 'none') return !r.teamId;
    if (sel.kind === 'team') return r.teamId === sel.id;
    if (!r.teamId) return false;
    const t = orgTeams.find((x) => x.id === r.teamId);
    return !!t && t.divisionId === sel.id;
  }, [orgTeams]);

  /**
   * ★★ 트리의 숫자와 목록의 숫자가 **한 벌에서 나온다**.
   *    종전 머리의 「부서 없음 N명」은 팀을 고르는 순간 **0** 이 됐다 — 걸러진 목록을 세고 있었다.
   */
  const counts = useMemo(() => {
    const byTeam = new Map<string, number>();
    let none = 0;
    for (const r of rows) {
      if (!r.teamId) { none += 1; continue; }
      byTeam.set(r.teamId, (byTeam.get(r.teamId) ?? 0) + 1);
    }
    const byDiv = new Map<string, number>();
    for (const t of orgTeams) {
      if (!t.divisionId) continue;
      byDiv.set(t.divisionId, (byDiv.get(t.divisionId) ?? 0) + (byTeam.get(t.id) ?? 0));
    }
    return { total: rows.length, none, byTeam, byDiv };
  }, [rows, orgTeams]);

  /** 오른쪽 목록 — 고른 조직으로 거른 것. */
  const scoped = useMemo(() => rows.filter((r) => inSel(r, orgSel)), [rows, orgSel, inSel]);

  /** 고른 자리의 이름 — 오른쪽 머리에 선다. */
  const selLabel = useMemo(() => {
    if (orgSel.kind === 'all') return '전체';
    if (orgSel.kind === 'none') return '회사 직속';
    if (orgSel.kind === 'div') return divs.find((d) => d.id === orgSel.id)?.name ?? '부서';
    return orgTeams.find((t) => t.id === orgSel.id)?.name ?? '팀';
  }, [orgSel, divs, orgTeams]);

  /** 이름 옆 배지 — 대표 · 부서장 · 팀장. 없으면 안 붙인다. */
  const dutyOf = useCallback((userId: string): string | null => {
    if (repId && userId === repId) return '대표';
    if (divLeadIds.has(userId)) return '부서장';
    if (teamLeadIds.has(userId)) return '팀장';
    return null;
  }, [repId, divLeadIds, teamLeadIds]);

  /**
   * 한 사람을 읽는다.
   *
   * ★ **알림을 지우지 않는다.** 쓰고 나면 이 함수가 다시 읽는데, 여기서 지우면 방금 낸
   *   「저장했습니다」 가 그 자리에서 사라진다(검사가 이것을 잡았다). 지우는 것은
   *   **사람을 바꿀 때**의 일이라 누르는 쪽에서 한다.
   */
  /**
   * 근무제 배정 (2026-09-02 · 사용자 지시 「인사정보에서 근무제관리에서 근무제를 가져와서
   * 설정할수있도록」). **새 API 를 만들지 않았다** — 목록도 배정도 근태가 쓰던 그 라우트다.
   * ★ 배정은 덮어쓰지 않는다: 서버가 열린 배정을 시행일 하루 전으로 닫고 새 줄을 쌓는다.
   *   덮어쓰면 지난 달 지각 판정이 소급해 달라진다.
   */
  const [schedules, setSchedules] = useState<WorkScheduleDto[]>([]);
  const [schOpen, setSchOpen] = useState(false);
  const [schPick, setSchPick] = useState('');
  const [schFrom, setSchFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [schBusy, setSchBusy] = useState(false);

  const openDetail = useCallback(async (userId: string) => {
    setSelected(userId);
    setMode('detail');
    setMoveTeam('');
    // ★ 사람이 바뀌면 「신상」으로 — 앞사람의 탭에 머물면 무엇을 보고 있는지 흐려진다.
    setDtab('PROFILE');
    try {
      const d = await getEmployee(userId);
      setDetail(d);
      setHrDraft({});
    } catch (err) {
      setError(ERRORS[codeOf(err)] ?? '직원 정보를 불러오지 못했습니다.');
    }
  }, []);

  // ★ 2026-09-13 (K) — 새 직원 명부 상세의 「계정 · 권한 · 부서 관리 →」 가 `?userId=` 로 이 화면을 연다. 그 사람을 바로 연다.
  const [searchParams] = useSearchParams();
  const openFromUrl = searchParams.get('userId');
  useEffect(() => { if (openFromUrl) void openDetail(openFromUrl); }, [openFromUrl, openDetail]);

  /**
   * 연차 목록 — 상세를 처음 열 때 **한 번만** 읽는다.
   * ★ 못 읽으면(옛 서버·권한) 그 카드만 안 뜬다 — 상세 전체를 죽이지 않는다.
   */
  useEffect(() => {
    if (!selected || quotas !== null) return;
    void listLeaveQuotas().then((d) => setQuotas(d.rows)).catch(() => setQuotas([]));
  }, [selected, quotas]);

  const run = async (key: string, fn: () => Promise<string>, fail: string) => {
    setBusy(key);
    setError(null);
    setDone(null);
    try {
      setDone(await fn());
      await Promise.all([load(search, status), selected ? openDetail(selected) : Promise.resolve()]);
    } catch (err) {
      setError(ERRORS[codeOf(err)] ?? fail);
    } finally {
      setBusy(null);
    }
  };

  const hrValue = (key: keyof EmployeeDetailDto['hr']): string => {
    if (key in hrDraft) return hrDraft[key] ?? '';
    const v = detail?.hr[key];
    return v === null || v === undefined ? '' : String(v);
  };

  const saveHr = () => {
    if (!selected || Object.keys(hrDraft).length === 0) return;
    void run('hr', async () => {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(hrDraft)) {
        body[k] = k === 'jobLevel' ? (v ? Number(v) : null) : (v || null);
      }
      const res = await updateEmployeeHr(selected, body);
      setHrDraft({});
      return res.annualDays === null
        ? '저장했습니다.'
        : `저장했습니다 — 올해 연차 ${res.annualDays}일로 다시 계산됐습니다.`;
    }, '저장하지 못했습니다.');
  };

  const onHire = () => {
    void run('hire', async () => {
      const res = await hireEmployee({
        name: hire.name.trim(),
        email: hire.email.trim(),
        employeeNumber: hire.employeeNumber.trim() || null,
        hireDate: hire.hireDate || null,
        position: hire.position.trim() || null,
        employmentType: hire.employmentType || null,
        teamId: hire.teamId || null,
      });
      setIssued({ password: res.initialPassword, annualDays: res.annualDays, who: hire.name.trim() });
      setHire(EMPTY_HIRE);
      return '계정을 만들었습니다.';
    }, '입사 처리를 하지 못했습니다.');
  };

  /** ★ 권한은 **내 등급 이하만**. 서버가 최종 판정하지만 화면이 먼저 안 준다. */
  const roleOptions = useMemo<ManagedRole[]>(() => assignableRolesFor(myRole), [myRole]);
  const canTouchRole = detail ? canManageRole(myRole, detail.account.role) : false;

  const changeRole = (role: string) => {
    if (!selected) return;
    void run('role', async () => {
      await changeUserRole(selected, role as AssignableRole);
      return `권한을 «${ROLE_OPTION_LABEL[role as ManagedRole] ?? role}» 로 바꿨습니다.`;
    }, '권한을 바꾸지 못했습니다.');
  };

  const resetPassword = () => {
    if (!selected) return;
    void run('pw', async () => {
      // ★ 임시 비밀번호는 **사번이 있어야** 서버가 낸다(사번 로그인에 쓰인다).
      //   사번이 없으면 초대 링크로 돌린다 — 둘 중 하나는 늘 낼 수 있어야 한다.
      const r = await resetAdminUserPassword(selected, 'temp_password');
      const pw = r.invite.tempPassword;
      if (pw) setIssued({ password: pw, annualDays: null, who: detail?.self.name ?? '' });
      return pw ? '임시 비밀번호를 냈습니다.' : '초대 링크를 다시 냈습니다.';
    }, '비밀번호를 바꾸지 못했습니다.');
  };

  /** ★ 팀 옮기기는 «빼고 넣기» 다 — 서버에 «옮기기» 가 없고, 둘로 하면 중간 상태가 남는다. */
  const moveToTeam = () => {
    if (!selected || !moveTeam) return;
    const fromId = detail?.hr.teamId ?? null;
    void run('team', async () => {
      if (fromId) await removeTeamMember(fromId, selected);
      await addTeamMember(moveTeam, { userId: selected });
      setMoveTeam('');
      return '부서를 옮겼습니다.';
    }, '부서를 옮기지 못했습니다.');
  };

  const toggleLeader = (next: boolean) => {
    const teamId = detail?.hr.teamId;
    if (!selected || !teamId) return;
    void run('leader', async () => {
      await setTeamMemberLeader(teamId, selected, next);
      return next ? '팀장으로 지정했습니다.' : '팀장 지정을 풀었습니다.';
    }, '바꾸지 못했습니다.');
  };

  /**
   * 사람을 팀으로 옮긴다 — **빼고 넣기**.
   *
   * ★ 명부 행이 아는 소속은 **하나**다(서버가 `take: 1`). 여러 팀에 걸친 사람은 그중 하나만
   *   빠지므로, 그때는 상세의 「부서 · 팀」에서 손으로 정리해야 한다 — 화면이 그렇게 말한다.
   * ★ 이미 그 팀이면 아무 일도 하지 않는다(헛일로 발령 이력을 만들지 않는다).
   */
  const dropOnTeam = async (r: EmployeeRowDto, teamId: string, teamName: string) => {
    setDropTeam(null);
    dragUser.current = null;
    if (r.teamId === teamId) return;
    setBusy('drag');
    setError(null);
    setDone(null);
    try {
      if (r.teamId) await removeTeamMember(r.teamId, r.userId);
      await addTeamMember(teamId, { userId: r.userId });
      setDone(`${r.name} 님을 ${teamName} 으로 옮겼습니다.`);
      await load(search, status);
    } catch (err) {
      setError(ERRORS[codeOf(err)] ?? '소속을 옮기지 못했습니다.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * 팀 줄에 붙이는 «떨굴 자리» 속성.
   * ★★ **`dragover` 에서 기본동작을 막지 않으면 `drop` 이 아예 안 온다** — 끌어다 놓기가
   *    조용히 안 되는 원인의 대부분이 이것이다(워크스페이스에서 한 번 겪었다).
   */
  const dropProps = (teamId: string, teamName: string) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!dragUser.current) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDropTeam(teamId);
    },
    onDragLeave: () => setDropTeam((p) => (p === teamId ? null : p)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const r = dragUser.current;
      if (r) void dropOnTeam(r, teamId, teamName);
    },
  });

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void load(search, status);
  };

  return (
    <div className="hrpage hrstaff">
      <header className="hrpage__head">
        <h1>직원 관리</h1>
        {/*
          ★ 시안 맞춤(2026-08-30) — 머리에 **세 숫자**를 적는다. 「부서 없음」과 「입사일 미설정」은
            그냥 알림이 아니라 **연차·조직 기능이 왜 안 도는지**의 답이다.
        */}
        <p>
          재직 <b>{counts.total}명</b> · 회사 직속 <b>{counts.none}명</b>
          {missing > 0 && <> · 입사일 미설정 <b>{missing}명</b></>}
        </p>
        {/* ★ 시안은 「입사 처리」가 **머리 오른쪽**이다 — 왼쪽 칸은 조직도의 자리다. */}
        <div className="hrstaff__headsp" />
        <Button
          variant="ghost"
          size="compact"
          className="hrstaff__hire"
          iconLeft={<Plus size={15} aria-hidden />}
          onClick={() => { setMode('hire'); setSelected(null); setDone(null); setError(null); }}
        >
          입사 처리
        </Button>
      </header>

      {error && <p className="hrcert__err" role="alert">{error}</p>}
      {done && <p className="hrjg__notice" role="status">{done}</p>}

      {/* ★ 초기 · 임시 비밀번호는 **지금 한 번만** 보인다 — 저장하지 않는다. */}
      {issued && (
        <div className="hrstaff__issued" role="status">
          <Key size={16} aria-hidden />
          <span>
            {issued.who ? <b>{issued.who}</b> : null} 비밀번호 <code>{issued.password}</code>
            {issued.annualDays !== null && <> · 올해 연차 {issued.annualDays}일</>}
            {' '}— <b>지금 한 번만 보입니다.</b> 옮겨 적고 닫으세요.
          </span>
          <Button variant="secondary" size="sm" onClick={() => setIssued(null)}>닫기</Button>
        </div>
      )}

      {/*
        ★★★ **상세는 본문을 통째로 쓴다** (2026-08-31 · 사용자 지적 — 「인사정보 상세화면은
           목록이 안 보여야 하는데 잘못 나온 듯하다」).
           시안 B2 의 본문에는 **조직도도 목록 머리도 없다** — 사람 하나를 보는 자리이기 때문이다.
           종전에는 왼쪽 조직도와 「전체 N명 · 찾기 · 재직」 줄이 그대로 남아 있었다.
        ★ 「← 목록」을 누르면 둘이 함께 돌아온다.
      */}
      <div className={selected ? 'hrstaff__solo' : 'hrstaff__split'}>
        {/*
          ★★★ **왼쪽은 조직도다** (2026-08-31 · 시안 B1 맞춤).
             종전 이 칸은 「명부」 머리 · 사람 찾기 · 재직 세그 · 트리가 뒤섞여 있었다.
             시안은 **왼쪽 240px = 부서 · 팀만**, 사람 찾기와 재직 상태는 **오른쪽 목록 머리**다.
             찾는 대상이 다른 두 검색(조직 / 사람)이 나란히 있으면 어느 칸에 무엇을 치는지 묻게 된다.
        */}
        {!selected && (
        <aside className="hrstaff__list" aria-label="조직 고르기">
          <div className="hrstaff__find">
            <label className="hrteam__find">
              <Search size={14} aria-hidden />
              <input
                value={treeQ} placeholder="부서 · 팀 찾기" aria-label="부서 팀 찾기"
                onChange={(e) => setTreeQ(e.target.value)}
              />
            </label>
          </div>

          <nav className="hrstaff__tree" aria-label="조직도">
            {/* 회사 — 뿌리. 숫자는 지금 재직 상태로 받은 사람 수다. */}
            <button
              type="button"
              className={`hrstaff__node hrstaff__node--root${orgSel.kind === 'all' ? ' is-on' : ''}`}
              aria-current={orgSel.kind === 'all'}
              onClick={() => { setOrgSel({ kind: 'all' }); setSelected(null); }}
            >
              <Building2 size={13} aria-hidden />
              <span>전체</span><span className="hrstaff__n">{counts.total}</span>
            </button>

            {divs
              .filter((d) => {
                const q = treeQ.trim();
                if (!q) return true;
                if (d.name.includes(q)) return true;
                return orgTeams.some((t) => t.divisionId === d.id && t.name.includes(q));
              })
              .map((d) => (
                <div key={d.id} className="hrstaff__grp">
                  {/* ★ 부서도 **고를 수 있다** — 종전에는 라벨이라 「이 본부 사람만」을 볼 수 없었다. */}
                  <button
                    type="button"
                    className={`hrstaff__node${orgSel.kind === 'div' && orgSel.id === d.id ? ' is-on' : ''}`}
                    aria-current={orgSel.kind === 'div' && orgSel.id === d.id}
                    onClick={() => { setOrgSel({ kind: 'div', id: d.id }); setSelected(null); }}
                  >
                    <Building2 size={13} aria-hidden />
                    <span>{d.name}</span><span className="hrstaff__n">{counts.byDiv.get(d.id) ?? 0}</span>
                  </button>
                  {orgTeams
                    .filter((t) => t.divisionId === d.id && (!treeQ.trim() || t.name.includes(treeQ.trim()) || d.name.includes(treeQ.trim())))
                    .map((t) => (
                      <button
                        key={t.id} type="button"
                        className={`hrstaff__node is-sub${orgSel.kind === 'team' && orgSel.id === t.id ? ' is-on' : ''}${dropTeam === t.id ? ' is-drop' : ''}`}
                        aria-current={orgSel.kind === 'team' && orgSel.id === t.id}
                        onClick={() => { setOrgSel({ kind: 'team', id: t.id }); setSelected(null); }}
                        {...dropProps(t.id, t.name)}
                      >
                        <Users size={12} aria-hidden />
                        <span>{t.name}</span><span className="hrstaff__n">{counts.byTeam.get(t.id) ?? 0}</span>
                      </button>
                    ))}
                </div>
              ))}

            {orgTeams.filter((t) => !t.divisionId && (!treeQ.trim() || t.name.includes(treeQ.trim()))).length > 0 && (
              <div className="hrstaff__grp">
                <p className="hrstaff__div">부서 없는 팀</p>
                {orgTeams
                  .filter((t) => !t.divisionId && (!treeQ.trim() || t.name.includes(treeQ.trim())))
                  .map((t) => (
                    <button
                      key={t.id} type="button"
                      className={`hrstaff__node is-sub${orgSel.kind === 'team' && orgSel.id === t.id ? ' is-on' : ''}${dropTeam === t.id ? ' is-drop' : ''}`}
                      aria-current={orgSel.kind === 'team' && orgSel.id === t.id}
                      onClick={() => { setOrgSel({ kind: 'team', id: t.id }); setSelected(null); }}
                      {...dropProps(t.id, t.name)}
                    >
                      <Users size={12} aria-hidden />
                      <span>{t.name}</span><span className="hrstaff__n">{counts.byTeam.get(t.id) ?? 0}</span>
                    </button>
                  ))}
              </div>
            )}

            {/* ★ 팀이 없는 사람의 자리 — 조직도와 **같은 말**을 쓴다(2026-08-31 · 「회사 직속」). */}
            {treeQ.trim() === '' && (
              <button
                type="button"
                className={`hrstaff__node${orgSel.kind === 'none' ? ' is-on' : ''}`}
                aria-current={orgSel.kind === 'none'}
                onClick={() => { setOrgSel({ kind: 'none' }); setSelected(null); }}
              >
                <Users size={12} aria-hidden />
                <span>회사 직속</span><span className="hrstaff__n">{counts.none}</span>
              </button>
            )}
          </nav>

          {missing > 0 && (
            <p className="hrstaff__warn">
              <AlertCircle size={13} aria-hidden />
              입사일이 없는 사람 {missing}명 — 연차가 시작되지 않습니다.
            </p>
          )}

          {/* ★★ 이 줄은 **이제 참말이다** — 끌어다 놓기를 만들고 나서 적는다(시안 callout 1).
                 없는 기능을 시안이 그렸다고 미리 적으면 그게 잘못된 안내가 된다. */}
          <p className="hrstaff__hintdrag">
            사람 행을 <b>팀 위로 끌어다 놓으면</b> 소속이 바뀝니다.
          </p>

          {/* ★ 시안의 자물쇠 줄 — 여기서 못 하는 일을 **여기서** 말한다. */}
          <p className="hrstaff__lock">
            부서 · 팀 <b>만들기는 통합설정</b>에서 합니다 — 여기서는 <b>사람</b>을 봅니다.
          </p>
        </aside>
        )}

        <div className="hrstaff__main">
          {/*
            ★★★ **목록 머리** (2026-08-31 · 시안 B1) — 고른 조직 이름 · 인원 · 사람 찾기 ·
               재직 상태가 **여기** 선다. 왼쪽은 조직도의 자리다.
          */}
          {mode !== 'hire' && !selected && (
            <div className="hrstaff__mainhead">
              <b className="hrstaff__scope">{selLabel}</b>
              <span className="hrpage__pill">{scoped.length}명</span>
              <div className="hrstaff__mainsp" />
              <form className="hrstaff__findone" onSubmit={submitSearch}>
                <label className="hrteam__find">
                  <Search size={14} aria-hidden />
                  <input
                    value={search} placeholder="이름 · 사번 · 이메일" aria-label="직원 찾기"
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
              </form>
              {/* ★ 재직 · 휴직 · 퇴사 — 서버가 이미 `status` 를 받는다(새 라우트 0). */}
              <div className="hrstaff__seg" role="group" aria-label="재직 상태">
                {([['ACTIVE', '재직'], ['LEAVE', '휴직'], ['RESIGNED', '퇴사']] as const).map(([v, label]) => (
                  <button
                    key={v} type="button" aria-pressed={status === v}
                    className={status === v ? 'is-on' : undefined}
                    onClick={() => { setStatus(v); setSelected(null); void load(search, v); }}
                  >{label}</button>
                ))}
              </div>
            </div>
          )}

          {/*
            ★★★ 표 (2026-08-30 · 시안 맞춤) — 종전은 왼쪽 좁은 칸에 **줄 나열**이라
               소속·사번·상태를 함께 볼 수 없었다. 시안이 표로 그린 이유가 그것이다.
            ★★ **사람을 고르면 목록 자리가 상세로 바뀐다**(시안 B1 의 말 그대로) — 종전에는
               표 아래에 상세가 붙어, 긴 표에서는 고른 사람이 화면 밖에 있었다.
          */}
          {mode !== 'hire' && !selected && (loading || scoped.length === 0) && (
            <p className="hrpage__empty">
              {loading ? '불러오는 중…' : (rows.length === 0 ? '직원이 없습니다.' : `${selLabel} 에 사람이 없습니다.`)}
            </p>
          )}
          {mode !== 'hire' && !selected && !loading && scoped.length > 0 && (
            <section className="hrpage__panel hrstaff__tblwrap" aria-label="직원 목록">
              <table className="hrpage__tbl hrstaff__tbl">
                <thead>
                  <tr>
                    <th scope="col">이름</th>
                    <th scope="col">소속</th>
                    <th scope="col">직위</th>
                    <th scope="col">사번</th>
                    <th scope="col">상태</th>
                    <th scope="col" aria-label="열기" />
                  </tr>
                </thead>
                <tbody>
                  {scoped.map((r) => {
                    const duty = dutyOf(r.userId);
                    const divName = r.teamId ? divNameOfTeam.get(r.teamId) ?? null : null;
                    return (
                      <tr
                        key={r.userId}
                        /* ★ 시안 callout 1 — 행을 끌어 왼쪽 팀에 떨구면 소속이 바뀐다. */
                        draggable={busy !== 'drag'}
                        onDragStart={(e) => {
                          dragUser.current = r;
                          // 파이어폭스는 데이터가 없으면 끌기를 시작하지 않는다. 읽지는 않는다.
                          e.dataTransfer.setData('text/plain', r.userId);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => { dragUser.current = null; setDropTeam(null); }}
                        onClick={() => { setDone(null); setError(null); void openDetail(r.userId); }}
                      >
                        <th scope="row">
                          <button type="button" className="hrstaff__rowbtn">
                            {/* ★ 시안의 이니셜 알약 — 사진은 이 목록에 오지 않는다(없는 것을 그리지 않는다). */}
                            <span className="hrstaff__av" aria-hidden="true">{r.name.slice(0, 1)}</span>
                            <span className="hrstaff__nm">{r.name}</span>
                            {duty && <span className="hrpage__pill hrstaff__duty">{duty}</span>}
                            {r.role !== 'STAFF' && (
                              <span className="hrpage__pill hrstaff__role">
                                {ROLE_OPTION_LABEL[r.role as ManagedRole] ?? r.role}
                              </span>
                            )}
                          </button>
                        </th>
                        <td className="hrpage__dim">
                          {/* ★ 소속은 «부서 · 팀» 두 마디다 — 팀 이름만으로는 어느 본부인지 모른다. */}
                          {r.teamName
                            ? (divName ? `${divName} · ${r.teamName}` : r.teamName)
                            : <span className="hrstaff__nota">회사 직속</span>}
                        </td>
                        <td>{r.position ?? <span className="hrpage__dim">—</span>}</td>
                        <td className="hrpage__dim hrstaff__num">{r.employeeNumber ?? '—'}</td>
                        <td>
                          <span className={`hrpage__pill hrstaff__st is-${r.employmentStatus.toLowerCase()}`}>
                            {EMPLOYMENT_STATUS_LABEL[r.employmentStatus] ?? r.employmentStatus}
                          </span>
                          {/* ★ 입사일이 없으면 연차가 안 돈다 — 표에서 바로 보이게. */}
                          {!r.hireDate && <span className="hrstaff__nota"> 입사일 없음</span>}
                        </td>
                        <td className="hrstaff__go" aria-hidden="true">›</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="hrpage__note">
                ⚠ <b>내려받기는 두지 않았습니다.</b> 이름 · 사번 · 이메일을 파일로 빼는 일이라
                <b> 서버가 기록을 남겨야</b> 하는데 그 길이 아직 없습니다 — 기록 없는 내려받기는
                만들지 않습니다.
              </p>
            </section>
          )}

          {mode === 'hire' ? (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                입사 처리
                <span className="hrpage__panels">계정을 만들고 부서에 넣습니다</span>
                <span className="hrjg__acts">
                  <Button variant="secondary" size="sm" onClick={() => setMode('detail')}>취소</Button>
                </span>
              </h2>
              <div className="hrstaff__form">
                <label className="hrcert__field">
                  <span>이름</span>
                  <input value={hire.name} onChange={(e) => setHire({ ...hire, name: e.target.value })} />
                </label>
                <label className="hrcert__field">
                  <span>로그인 이메일</span>
                  <input
                    type="email" value={hire.email} placeholder="hong@company.com"
                    onChange={(e) => setHire({ ...hire, email: e.target.value })}
                  />
                </label>
                <label className="hrcert__field">
                  <span>부서 · 팀</span>
                  <select value={hire.teamId} onChange={(e) => setHire({ ...hire, teamId: e.target.value })}>
                    <option value="">나중에</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
                <label className="hrcert__field">
                  <span>입사일</span>
                  <input type="date" value={hire.hireDate} onChange={(e) => setHire({ ...hire, hireDate: e.target.value })} />
                </label>
                <label className="hrcert__field">
                  <span>직위</span>
                  {/* 직위는 마스터에서 고른다(판 2) — 목록 밖 값은 막지 않고 알린다. */}
                  <JobGradeSelect
                    value={hire.position} manage
                    onChange={(v) => setHire({ ...hire, position: v })}
                  />
                </label>
                <label className="hrcert__field">
                  <span>사번</span>
                  <input value={hire.employeeNumber} onChange={(e) => setHire({ ...hire, employeeNumber: e.target.value })} />
                </label>
              </div>
              <div className="hrstaff__formfoot">
                <Button variant="primary" onClick={onHire} disabled={busy === 'hire' || !hire.name.trim() || !hire.email.trim()}>
                  {busy === 'hire' ? '만드는 중…' : '계정 만들기'}
                </Button>
                {/* ★ 권한은 여기서 안 준다 — 만든 뒤 «계정» 에서 고른다. 만들 때 같이 주면
                    실수로 관리자를 하나 더 만들기 쉽다. */}
                <span className="hrpage__dim">권한은 만든 뒤 «계정» 에서 정합니다 — 기본은 직원입니다.</span>
              </div>
            </section>
          ) : !detail ? (
            /* ★ 사람을 고르면 본문이 통째로 상세가 되므로, 이 줄은 «읽는 중» 일 때만 잠깐 선다. */
            selected ? <p className="hrpage__empty">불러오는 중…</p> : null
          ) : (
            <>
              {/*
                ★★★ **목록으로 돌아가는 길** (2026-08-31) — 상세가 목록 «자리»를 쓰게 되면서
                   돌아갈 단추가 있어야 한다. 종전에는 표가 위에 그대로 있어 필요 없었다.
                   ⚠ 이것이 없으면 사람을 한 번 고른 뒤 **목록을 다시 볼 길이 없다**.
              */}
              {/*
                ★★★ **시안 B2 의 머리** (2026-08-31 · 사용자 지시 「직원 상세도 만들어줘」).
                   ← 목록 · 큰 이니셜 · 이름 + 상태·역할 배지 · 한 줄 부제.
                ⚠ 「← 목록」이 없으면 사람을 한 번 고른 뒤 **목록을 다시 볼 길이 없다**
                   (상세가 목록 «자리»를 쓰기 때문이다).
              */}
              {/*
                ★★★ **머리는 공용 한 벌이다**(2026-09-02 · 시안 승인) — 인사 카드와 같은
                   `PersonHead` 를 쓴다. 사진 112px 이 서고, 인사 담당은 그 자리에서 바꾼다.
                   종전에는 이니셜만 그렸고 사진은 아예 없었다(서버는 주고 있었는데도).
              */}
              <PersonHead
                userId={selected ?? ''}
                name={detail.self.name}
                avatarUrl={detail.self.avatarUrl}
                canEditAvatar
                onAvatarChange={() => { if (selected) void openDetail(selected); }}
                back={(
                  <Button
                    variant="secondary" size="sm" className="hrstaff__back"
                    onClick={() => { setSelected(null); setDetail(null); setDone(null); setError(null); }}
                  >
                    ← 목록
                  </Button>
                )}
                badges={(
                  <>
                    <span className={`hrpage__pill hrstaff__st is-${detail.hr.employmentStatus.toLowerCase()}`}>
                      {EMPLOYMENT_STATUS_LABEL[detail.hr.employmentStatus] ?? detail.hr.employmentStatus}
                    </span>
                    {dutyOf(selected ?? '') && (
                      <span className="hrpage__pill hrstaff__duty">{dutyOf(selected ?? '')}</span>
                    )}
                    {detail.account.role !== 'STAFF' && (
                      <span className="hrpage__pill hrstaff__role">
                        {ROLE_OPTION_LABEL[detail.account.role as ManagedRole] ?? detail.account.role}
                      </span>
                    )}
                  </>
                )}
                subtitle={[
                  [detail.hr.divisionName, detail.hr.teamName, detail.hr.position].filter(Boolean).join(' · ') || '회사 직속',
                  detail.account.employeeNumber ? `사번 ${detail.account.employeeNumber}` : null,
                  detail.hr.tenureLabel ? `근속 ${detail.hr.tenureLabel}` : null,
                ].filter(Boolean).join(' · ')}
                facts={[
                  { label: '메일', value: detail.account.email },
                  { label: '휴대전화', value: detail.self.phone ?? '—', numeric: true },
                  { label: '내선', value: detail.self.extension ?? '—', numeric: true },
                ]}
                actions={(
                  <>
                    {/* ★ 이 화면에서 하는 일이 아니라 **가는 길**이다. */}
                    <Button variant="secondary" size="compact" href="/erp/hr/assignments">인사발령 등록</Button>
                    <Button variant="secondary" size="compact" href="/erp/hr/certificates">증명서</Button>
                  </>
                )}
              />

              {/*
                ★★★ **탭 일곱** — 시안 B2 그대로. 발령·경력·포상·평가·교육은 종전까지
                   「인사 카드」라는 **다른 화면**에 있어서, 담당자가 한 사람을 보려고 화면을
                   둘 열어야 했다. 이제 한 자리에서 본다(내용은 같은 한 벌을 나눠 쓴다).
              */}
              {/*
                ★ 정본 E5 의 `line` — 다른 내용으로 가는 **주 탭**이다(필터가 아니다).
                  아래 줄이 「위는 사람, 아래는 기록」을 가르는 경계선을 겸한다.
                  ⚠ 손으로 짠 `hrcert__tabs` 를 걷어냈다 — 칸 이름(`hrcert__tab`)을 빠뜨리면
                    단추가 민무늬 글자로 죽 붙어 버리는 함정이 있었고, 인사 카드가 실제로 그랬다.
              */}
              <Tabs
                className="hrstaff__dtabs"
                ariaLabel="상세 탭"
                items={DETAIL_TABS.map((t) => ({ value: t.key, label: t.label }))}
                value={dtab}
                onChange={(v) => setDtab(v as DetailTab)}
              />

              {/* 기록 다섯 — 인사 카드와 **같은 한 벌**. */}
              {dtab !== 'PROFILE' && dtab !== 'ACCOUNT' && selected && (
                <StaffRecordSection userId={selected} tab={dtab} />
              )}

              {dtab === 'ACCOUNT' && (
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  <Key size={15} aria-hidden /> 계정 · 권한
                  <span className="hrpage__panels">로그인과 등급</span>
                </h2>
                <table className="hrpage__tbl hrteam__tbl">
                  <tbody>
                    <tr>
                      <td>로그인</td>
                      <td>{detail.account.email} <span className="hrpage__dim">· 로그인 식별자라 여기서 안 바꿉니다</span></td>
                    </tr>
                    <tr>
                      <td>권한</td>
                      <td className="hrstaff__cell">
                        {canTouchRole && roleOptions.length > 0 ? (
                          <select
                            value={detail.account.role} aria-label="권한"
                            disabled={busy === 'role'}
                            onChange={(e) => changeRole(e.target.value)}
                          >
                            {roleOptions.map((r) => (
                              <option key={r} value={r}>{ROLE_OPTION_LABEL[r]}</option>
                            ))}
                          </select>
                        ) : (
                          <>
                            {ROLE_OPTION_LABEL[detail.account.role as ManagedRole] ?? detail.account.role}
                            {/* ★ 왜 못 고치는지 적는다 — 흐린 칸만 두면 고장으로 읽힌다. */}
                            <span className="hrpage__dim"> · 내 등급으로는 바꿀 수 없습니다</span>
                          </>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>비밀번호</td>
                      <td className="hrstaff__cell">
                        <Button variant="secondary" size="sm" onClick={resetPassword} disabled={busy === 'pw' || !canTouchRole}>
                          {busy === 'pw' ? '내는 중…' : '임시 비밀번호 내기'}
                        </Button>
                        <span className="hrpage__dim">낸 값은 그 자리에서 한 번만 보입니다</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
              )}

              {/*
                ★★★ **신상 탭 = 좌(기본 · 소속) + 우 300px** — 시안 B2 의 두 단.
                   오른쪽은 「지금 이 사람이 어떤 상태인가」를 숫자로 말하는 자리다.
              */}
              {dtab === 'PROFILE' && (
              <div className="hrstaff__d2">
              <div className="hrstaff__dcol">

              {/*
                ★★ **연락 — 「본인이 고치는 값」** (시안 B2). 담당자는 여기를 **고치지 않는다**.
                   ⚠ 그렇다고 **감추지는 않는다** — 감추면 담당자가 「연락처가 비었나」를 확인할
                     데가 없어진다. 잠그고, 왜 잠겼는지 적는다(내 인사기록카드가 쓰는 규약).
              */}
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  <Users size={15} aria-hidden /> 연락
                  <span className="hrpage__panels">본인이 고치는 값</span>
                </h2>
                <table className="hrpage__tbl hrteam__tbl">
                  <tbody>
                    <tr><td>이메일</td><td>{detail.account.email}</td></tr>
                    <tr><td>휴대전화</td><td>{detail.self.phone ?? <span className="hrpage__dim">—</span>}</td></tr>
                    <tr><td>내선</td><td>{detail.self.extension ?? <span className="hrpage__dim">—</span>}</td></tr>
                  </tbody>
                </table>
                <p className="hrpage__note">
                  담당자는 <b>연락처를 고치지 않습니다</b> — 본인이 «내 프로필» 에서 바꿉니다.
                  틀렸으면 <b>정정 요청</b>으로 알려 주세요.
                </p>
              </section>

              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  <Building2 size={15} aria-hidden /> 부서 · 팀
                </h2>
                <table className="hrpage__tbl hrteam__tbl">
                  <tbody>
                    <tr>
                      <td>지금 소속</td>
                      <td>
                        {detail.hr.teamName
                          ? [detail.hr.divisionName, detail.hr.teamName].filter(Boolean).join(' · ')
                          : <span className="hrstaff__nota">부서 없음 — 결재 사다리의 팀장 칸이 빕니다</span>}
                      </td>
                    </tr>
                    <tr>
                      <td>옮기기</td>
                      <td className="hrstaff__cell">
                        <select value={moveTeam} aria-label="옮길 부서" onChange={(e) => setMoveTeam(e.target.value)}>
                          <option value="">고르세요</option>
                          {teams.filter((t) => t.id !== detail.hr.teamId).map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                        <Button variant="secondary" size="sm" onClick={moveToTeam} disabled={!moveTeam || busy === 'team'}>
                          {busy === 'team' ? '옮기는 중…' : '옮기기'}
                        </Button>
                      </td>
                    </tr>
                    {detail.hr.teamId && (
                      <tr>
                        <td>팀장</td>
                        <td className="hrstaff__cell">
                          <Button variant="secondary" size="sm" onClick={() => toggleLeader(true)} disabled={busy === 'leader'}
                          >
                            팀장으로 지정
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => toggleLeader(false)} disabled={busy === 'leader'}
                          >
                            지정 풀기
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </section>

              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  <Users size={15} aria-hidden /> 인사 정보
                  <span className="hrjg__acts">
                    <Button variant="secondary" size="sm" onClick={saveHr} disabled={busy === 'hr' || Object.keys(hrDraft).length === 0 || !detail.editable.HR}>
                      {busy === 'hr' ? '저장 중…' : <><Save size={14} aria-hidden /> 저장</>}
                    </Button>
                  </span>
                </h2>
                {!detail.editable.HR && (
                  <p className="hrstaff__warn">
                    <AlertCircle size={13} aria-hidden /> 이 묶음은 인사 담당만 고칠 수 있습니다 — 여기서는 보기만 합니다.
                  </p>
                )}
                <div className="hrstaff__form">
                  <label className="hrcert__field">
                    <span>입사일</span>
                    <input
                      type="date" value={hrValue('hireDate')} disabled={!detail.editable.HR}
                      onChange={(e) => setHrDraft({ ...hrDraft, hireDate: e.target.value })}
                    />
                  </label>
                  <label className="hrcert__field">
                    <span>직위</span>
                    {/* 직급 레벨 칸은 뺐다(판 2) — 레벨은 «직위» 가 갖고 있고,
                        맞추는 일은 직위·직급 마스터의 「레벨 맞추기」가 한 번에 한다. */}
                    <JobGradeSelect
                      value={hrValue('position')} disabled={!detail.editable.HR} manage
                      onChange={(v) => setHrDraft({ ...hrDraft, position: v })}
                    />
                  </label>
                  <label className="hrcert__field">
                    <span>고용형태</span>
                    <select
                      value={hrValue('employmentType')} disabled={!detail.editable.HR}
                      onChange={(e) => setHrDraft({ ...hrDraft, employmentType: e.target.value })}
                    >
                      <option value="">고르세요</option>
                      {employmentTypes.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="hrstaff__foot">
                  <Check size={13} aria-hidden />
                  근무제 <b>{detail.schedule.name}</b> {detail.schedule.baseStart} – {detail.schedule.baseEnd}
                  {detail.hr.annualDays !== null && <> · 올해 연차 {detail.hr.annualDays}일</>}
                  {' '}· 인사 변경은 감사에 남습니다.
                </p>
              </section>

              </div>

              {/* ── 오른쪽 300px — 시안 B2 의 곁 카드 ── */}
              <aside className="hrstaff__dside">
                {(() => {
                  const q = quotas?.find((x) => x.userId === selected) ?? null;
                  const total = q?.annualTotal ?? detail.hr.annualDays ?? null;
                  const remain = q?.remainDays ?? null;
                  const used = q?.usedDays ?? null;
                  /* ★ 총계를 모르면 카드를 안 그린다 — 「0일 남음」은 지어낸 말이 된다. */
                  if (total === null) {
                    return (
                      <section className="hrpage__panel hrstaff__side">
                        <h3 className="hrstaff__sidet">휴가 · 연차</h3>
                        <p className="hrpage__empty">
                          입사일이 없어 연차가 시작되지 않았습니다.
                        </p>
                      </section>
                    );
                  }
                  const pct = total > 0 && used !== null ? Math.min(100, Math.round((used / total) * 100)) : null;
                  return (
                    <section className="hrpage__panel hrstaff__side">
                      <h3 className="hrstaff__sidet">휴가 · 연차</h3>
                      <p className="hrstaff__big">
                        <b>{remain ?? total}</b>
                        <span>일 남음 / {total}일</span>
                      </p>
                      {pct !== null && (
                        <>
                          <div className="hrstaff__bar" aria-hidden="true"><i style={{ width: `${pct}%` }} /></div>
                          <p className="hrstaff__sidesub">소진율 {pct}% · 쓴 날 {used}일</p>
                        </>
                      )}
                      {/*
                        ★★★ **넘겨 쓴 만큼을 적는다** (2026-09-11 · A-1).

                           「일 남음」은 서버에서 `Math.max(0, …)` 로 0 에서 멈춘다. 그래서
                           **부여를 다 쓴 사람과 이틀을 더 쓴 사람이 이 카드에서 똑같이
                           「0일 남음」** 이었다 — 정작 손을 써야 하는 담당자가 보는 화면인데.

                        ★ 넘겼을 때만 적는다(정본 P7 「셀 수 없는 것은 적지 않는다」) —
                          안 넘겼으면 0, 모르면 `null`, 옛 서버면 `undefined` 라 **아무 말도 안 한다**.
                      */}
                      {typeof q?.overdrawnDays === 'number' && q.overdrawnDays > 0 && (
                        <p className="hrstaff__sidebad">
                          연차 초과 {q.overdrawnDays}일 — 부여보다 더 썼습니다
                        </p>
                      )}
                      {q?.adjustTotal ? (
                        <p className="hrstaff__sidesub">조정 {q.adjustTotal > 0 ? '+' : ''}{q.adjustTotal}일{q.adjustReason ? ` · ${q.adjustReason}` : ''}</p>
                      ) : null}
                    </section>
                  );
                })()}

                <section className="hrpage__panel hrstaff__side">
                  <h3 className="hrstaff__sidet">근무제</h3>
                  <p className="hrstaff__big"><b>{detail.schedule.baseStart}</b><span>– {detail.schedule.baseEnd}</span></p>
                  <p className="hrstaff__sidesub">{detail.schedule.name}</p>
                  {/*
                    ★★★ **«어디서 왔나» 를 적는다**(2026-09-02). 개인 배정이 없어도 팀이나
                       회사 기본으로 **이미 적용되고 있다** — 출처를 안 적으면 담당자가
                       「비었다」로 읽고 개인 배정을 새로 만든다.
                    ★ 옛 서버는 `source` 를 안 준다 — 없으면 그 줄을 안 그린다.
                  */}
                  {detail.schedule.source && (
                    <p className="hrstaff__sidesub">
                      <span className="hrpage__pill hrstaff__schsrc">
                        {SCHEDULE_SOURCE_LABEL[detail.schedule.source] ?? detail.schedule.source}
                      </span>
                    </p>
                  )}
                  <Button
                    variant="secondary" size="sm" className="hrstaff__schbtn"
                    disabled={schedules.length === 0}
                    onClick={() => {
                      setError(null); setDone(null);
                      setSchPick(detail.schedule.id ?? schedules[0]?.id ?? '');
                      setSchOpen(true);
                    }}
                  >
                    근무제 바꾸기
                  </Button>
                  {schedules.length === 0 && (
                    <p className="hrstaff__sidesub">
                      근무제를 읽지 못했습니다 — <Link className="hrjg__link" to="/erp/attendance/work-schedules">근무제 관리</Link>
                    </p>
                  )}
                </section>

                {schOpen && (
                  <Modal
                    size="sm"
                    title={`근무제 배정 — ${detail.self.name}`}
                    onClose={() => setSchOpen(false)}
                    footer={(
                      <ModalActions>
                        <Button variant="secondary" onClick={() => setSchOpen(false)}>취소</Button>
                        <Button
                          variant="primary" disabled={schBusy || !schPick}
                          onClick={() => {
                            setSchBusy(true); setError(null); setDone(null);
                            void saveWorkAssignments([{
                              scope: 'USER', scheduleId: schPick,
                              userId: selected, teamId: null, effectiveFrom: schFrom,
                            }])
                              .then(async () => {
                                setDone('근무제를 배정했습니다 — 이전 배정은 하루 전으로 닫혔습니다(지난 판정은 그대로).');
                                setSchOpen(false);
                                if (selected) await openDetail(selected);
                              })
                              .catch((e) => {
                                const st = (e as { response?: { status?: number } })?.response?.status;
                                setError(st === 403
                                  ? '근무제 배정은 회사 관리자만 할 수 있습니다.'
                                  : '배정하지 못했습니다.');
                              })
                              .finally(() => setSchBusy(false));
                          }}
                        >
                          배정
                        </Button>
                      </ModalActions>
                    )}
                  >
                    <div className="hrstaff__schform">
                      <FormField label="근무제" required>
                        {/* ★ 목록은 근태가 쓰는 그 API 다 — 새로 만들지 않았다. 중지한 것은 빠져 있다. */}
                        <Select
                          value={schPick || null} searchable placeholder="고르기"
                          options={schedules.map((x) => ({
                            value: x.id,
                            label: `${x.name} · ${x.startTime}–${x.endTime}`
                              + (WORK_TYPE_LABEL[x.type] && WORK_TYPE_LABEL[x.type] !== x.name ? ` · ${WORK_TYPE_LABEL[x.type]}` : ''),
                          }))}
                          onChange={setSchPick}
                        />
                      </FormField>
                      <FormField label="시행일">
                        <Input type="date" value={schFrom} onChange={(e) => setSchFrom(e.target.value)} />
                      </FormField>
                    </div>
                    <p className="hrstaff__schnote">
                      같은 사람의 <b>열린 배정은 시행일 하루 전으로 닫히고</b> 새 줄이 쌓입니다 —
                      덮어쓰지 않으므로 <b>지난 달 판정은 그대로</b>입니다.
                      개인 배정은 팀·회사 기본을 이깁니다.
                    </p>
                  </Modal>
                )}

                {/*
                  ⚠ 시안의 「오늘 근태」 카드는 **안 만들었다** — 남의 오늘 출퇴근을 주는 라우트가
                    없다(있는 것은 본인 것과 팀 전체다). 한 사람을 보려고 팀 전체를 받아 오기보다,
                    **모른다고 말하고 가는 길만** 낸다. 지어낸 시각을 그리지 않는다.
                */}
                <section className="hrpage__panel hrstaff__side">
                  <h3 className="hrstaff__sidet">근태</h3>
                  <p className="hrstaff__sidesub">
                    이 사람의 오늘 출퇴근은 여기서 못 봅니다 — <b>근태 현황</b>에서 조직으로 봅니다.
                  </p>
                  <Link className="hrjg__link" to="/erp/attendance/company">근태 현황 열기</Link>
                </section>
              </aside>
              </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
